import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Upload } from 'lucide-react';
import { HelpTip } from '../components/portal/HelpTip';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const Question = ({ q, value, onChange, locked, confirm }) => (
  <div className="space-y-1" data-testid={`q-${q.key}`}>
    <div className="text-sm">{q.label} {confirm && <Badge variant="secondary">Please confirm</Badge>}</div>
    {q.type === 'yesno' ? (
      <div className="flex gap-2">{['yes', 'no'].map((o) => (
        <Button key={o} size="sm" variant={value === o ? 'default' : 'outline'} disabled={locked} onClick={() => onChange(o)} data-testid={`q-${q.key}-${o}`}>{o === 'yes' ? 'Yes' : 'No'}</Button>))}</div>
    ) : q.type === 'textarea' ? <Textarea disabled={locked} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.key}-input`} />
      : <Input type={q.type === 'number' ? 'number' : 'text'} disabled={locked} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.key}-input`} />}
  </div>
);

const visible = (q, a) => !q.when || Object.entries(q.when).every(([k, v]) => a[k] === v);

const InfoRequest = ({ r, tid, onDone }) => {
  const [text, setText] = useState('');
  const respond = () => api.post(`/api/portal/tax-returns/${tid}/info-requests/${r.id}/respond`, { response: text }).then(() => { toast.success('Sent'); onDone(); });
  const upload = async (f) => {
    const fd = new FormData(); fd.append('file', f); fd.append('info_request_id', r.id);
    try { await api.post(`/api/portal/tax-returns/${tid}/upload`, fd); toast.success('Uploaded'); onDone(); } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };
  return (
    <div className="rounded-lg border border-border p-4 space-y-2" data-testid={`info-request-${r.id}`}>
      <div className="text-sm font-medium">{r.text} <Badge variant="outline">{r.status === 'open' ? 'Waiting for you' : 'Answered'}</Badge></div>
      {r.status === 'open' ? (
        <div className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Your reply" data-testid={`info-request-reply-${r.id}`} />
          <label className="text-sm underline cursor-pointer flex items-center gap-1"><Upload className="w-4 h-4" />File<input type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} /></label>
          <Button size="sm" onClick={respond} data-testid={`info-request-send-${r.id}`}>Send</Button>
        </div>
      ) : <p className="text-xs text-muted-foreground">{r.response}</p>}
    </div>
  );
};

const Detail = ({ tid }) => {
  const [v, setV] = useState(null);
  const [answers, setAnswers] = useState({});
  const [confirmed, setConfirmed] = useState([]);
  const timer = useRef(null);
  const ver = useRef(null);
  const load = useCallback(() => api.get(`/api/portal/tax-returns/${tid}`).then(({ data }) => { setV(data); setAnswers(data.answers); ver.current = data.version; }), [tid]);
  useEffect(() => { load(); }, [load]);
  if (!v) return null;

  const save = async (autosave, a = answers, c = confirmed) => {
    try {
      const { data } = await api.put(`/api/portal/tax-returns/${tid}/questionnaire`, { answers: a, confirmed: c, autosave, _version: ver.current });
      ver.current = data.version;
    } catch (e) {
      if (e.response?.status === 409) { toast.error(e.response.data.detail, { action: { label: 'Reload', onClick: load } }); return; }
      throw e;
    }
    if (!autosave) { toast.success('Draft saved'); load(); }
  };
  const set = (k, val) => {
    const a = { ...answers, [k]: val }; const c = [...confirmed, k];
    setAnswers(a); setConfirmed(c);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(true, a, c).catch(() => {}), 1200);
  };
  const submit = async () => {
    try { await save(true); const { data } = await api.post(`/api/portal/tax-returns/${tid}/submit`); toast.success(data.already_submitted ? 'Already submitted' : 'Submitted to your accountant'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not submit'); }
  };

  return (
    <div className="space-y-8" data-testid="tax-return-detail">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base md:text-lg font-semibold">{v.entity_name} · FY {v.financial_year}</h2>
        <Badge data-testid="tax-return-portal-status">{v.portal_status.replace('_', ' ')}</Badge>
        {v.locked && <Badge variant="outline"><Lock className="w-3 h-3 mr-1" />Submitted – locked</Badge>}
      </div>
      {v.info_requests.length > 0 && <div className="space-y-3"><p className="text-sm font-medium">Requests from your accountant</p>{v.info_requests.map((r) => <InfoRequest key={r.id} r={r} tid={tid} onDone={load} />)}</div>}
      {v.sections.map((s) => (
        <section key={s.title} className="space-y-4">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{s.title}</h3>
          {s.questions.filter((q) => visible(q, answers)).map((q) => <Question key={q.key} q={q} value={answers[q.key]} locked={v.locked} confirm={v.confirm_keys.includes(q.key) && !confirmed.includes(q.key)} onChange={(val) => set(q.key, val)} />)}
          {s.questions.some((q) => q.key === 'rental') && answers.rental === 'yes' && v.rental_years.map((r) => <Link key={r.property_id} className="text-sm underline block" to={r.link} data-testid={`rental-link-${r.property_id}`}>Rental details: {r.name}</Link>)}
        </section>
      ))}
      {v.checklist.length > 0 && <div><p className="text-sm font-medium">Documents we'll need</p><ul className="list-disc ml-5 text-sm">{v.checklist.map((c) => <li key={c}>{c}</li>)}</ul></div>}
      <div><p className="text-sm font-medium">Uploaded</p>{v.documents.map((d) => <p key={d.id} className="text-sm">{d.filename}</p>)}</div>
      {!v.locked && <div className="flex gap-3"><Button variant="outline" onClick={() => save(false)} data-testid="tax-return-save">Save draft</Button><Button onClick={submit} data-testid="tax-return-submit">Submit</Button></div>}
      <div><p className="text-sm font-medium">History</p>{v.history.map((h) => <p key={h.at} className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString('en-AU')} · {h.event}{h.note ? ` – ${h.note}` : ''}</p>)}</div>
      <Link className="text-sm underline" to={`/communications?topic=tax_return&ref=${v.id}`} data-testid="tax-return-message-link">Message your accountant about this</Link>
    </div>
  );
};

export default function MyTaxReturns() {
  const { id } = useParams();
  const [list, setList] = useState(null);
  useEffect(() => { api.get('/api/portal/tax-returns').then(({ data }) => setList(data)).catch(() => {}); }, []);
  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-8" data-testid="my-tax-returns-page">
      <h1 className="text-4xl sm:text-5xl font-headings font-bold">Tax returns</h1>
      <p className="text-sm text-muted-foreground"><HelpTip term="FY">Financial year</HelpTip> · <HelpTip term="taxable income">Taxable income</HelpTip> · <HelpTip term="depreciation">Depreciation</HelpTip></p>
      <div className="flex flex-wrap gap-2">
        {list?.returns.map((r) => <Link key={r.id} to={`/my-tax-returns/${r.id}`} data-testid={`tax-return-fy-${r.financial_year}`}>
          <Badge variant={r.id === id ? 'default' : 'outline'} className="text-sm px-3 py-1">FY {r.financial_year} · {r.portal_status.replace('_', ' ')}{r.open_requests ? ` · ${r.open_requests} request(s)` : ''}</Badge></Link>)}
        {list && !list.returns.length && <p className="text-sm text-muted-foreground" data-testid="tax-returns-empty">No tax returns have been started for {list.entity_name} yet.</p>}
      </div>
      {id && <Detail tid={id} />}
    </div>
  );
}
